"use client";

import { c as _c } from "react/compiler-runtime";
import { CircleAlert, CircleCheck, LockKeyhole } from "lucide-react";
import { ActionButton } from "@/features/home-v2/form/Action";
import { Dialog } from "@/features/home-v2/ui";
import type { BidItem } from "@/features/bids/types";
import { useBidDecision, type BidDecision } from "@/features/bids/useBidDecision";
import type { TransactionRecord } from "@/lib/transaction-flow";
import type { AuctionItem } from "../../types";
import { formatPrice } from "./format";
type Props = {
  mode: BidDecision;
  auction: AuctionItem;
  bid: BidItem;
  lang: "fa" | "en";
  onClose: () => void;
  onRecord: (entry: TransactionRecord) => void;
  onSuccess: () => Promise<void> | void;
  onQueued?: () => void;
};

/**
 * The owner's decision on a bid, in v2 — `useBidDecision`, AcceptBidFlow's
 * flow (one review, one confirm, one request, a recorded result), said in the
 * owner's words: who, how much, what this changes, and that it is final.
 */
export default function BidDecisionDialog(t0) {
  const $ = _c(41);
  const {
    mode,
    auction,
    bid,
    lang,
    onClose,
    onRecord,
    onSuccess,
    onQueued
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  if ($[2] !== auction || $[3] !== bid || $[4] !== lang || $[5] !== mode || $[6] !== onQueued || $[7] !== onRecord || $[8] !== onSuccess) {
    t2 = {
      auction,
      bid,
      mode,
      lang,
      onRecord,
      onSuccess,
      onQueued
    };
    $[2] = auction;
    $[3] = bid;
    $[4] = lang;
    $[5] = mode;
    $[6] = onQueued;
    $[7] = onRecord;
    $[8] = onSuccess;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const d = useBidDecision(t2);
  let t3;
  if ($[10] !== d.isReject || $[11] !== d.isSetWinner || $[12] !== t) {
    t3 = d.isReject ? t("\u0631\u062F \u067E\u06CC\u0634\u0646\u0647\u0627\u062F", "Reject the bid") : d.isSetWinner ? t("\u0627\u0646\u062A\u062E\u0627\u0628 \u0628\u0631\u0646\u062F\u0647", "Choose the winner") : t("\u062A\u0623\u06CC\u06CC\u062F \u067E\u06CC\u0634\u0646\u0647\u0627\u062F", "Accept the bid");
    $[10] = d.isReject;
    $[11] = d.isSetWinner;
    $[12] = t;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const title = t3;
  let t4;
  if ($[14] !== d.isReject || $[15] !== d.isSetWinner || $[16] !== t) {
    t4 = d.isReject ? t("\u0627\u06CC\u0646 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u06A9\u0646\u0627\u0631 \u06AF\u0630\u0627\u0634\u062A\u0647 \u0645\u06CC\u200C\u0634\u0648\u062F\u061B \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u062F\u0647\u0646\u062F\u0647 \u0628\u0631\u0627\u06CC \u0627\u062F\u0627\u0645\u0647 \u0628\u0627\u06CC\u062F \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u062A\u0627\u0632\u0647\u200C\u0627\u06CC \u0628\u062F\u0647\u062F.", "This bid is set aside \u2014 the bidder would have to make a new one.") : d.isSetWinner ? t("\u0627\u06CC\u0646 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0628\u0631\u0646\u062F\u0647\u200C\u06CC \u0646\u0647\u0627\u06CC\u06CC \u0627\u06CC\u0646 \u0645\u0632\u0627\u06CC\u062F\u0647 \u0645\u06CC\u200C\u0634\u0648\u062F. \u0628\u0639\u062F \u0627\u062B\u0631 \u0628\u0647 \u0628\u0631\u0646\u062F\u0647 \u0648 \u0645\u0628\u0644\u063A \u0628\u0647 \u0634\u0645\u0627 \u0645\u0646\u062A\u0642\u0644 \u0645\u06CC\u200C\u0634\u0648\u062F\u061B \u0627\u06CC\u0646 \u06A9\u0627\u0631 \u0631\u0648\u06CC \u0634\u0628\u06A9\u0647 \u0686\u0646\u062F \u062F\u0642\u06CC\u0642\u0647 \u0637\u0648\u0644 \u0645\u06CC\u200C\u06A9\u0634\u062F \u0648 \u062A\u0627 \u062A\u0645\u0627\u0645 \u0634\u0648\u062F\u060C \u0645\u0632\u0627\u06CC\u062F\u0647 \xAB\u062F\u0631 \u062D\u0627\u0644 \u062A\u0633\u0648\u06CC\u0647\xBB \u0627\u0633\u062A.", "This bid becomes the auction's final winner. The work then goes to the winner and the payment to you \u2014 on chain, it takes a few minutes, and until it's done the auction reads \xABSettling\xBB.") : t("\u0627\u06CC\u0646 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u062A\u0623\u06CC\u06CC\u062F \u0645\u06CC\u200C\u0634\u0648\u062F. \u0627\u0646\u062A\u062E\u0627\u0628 \u0628\u0631\u0646\u062F\u0647\u200C\u06CC \u0646\u0647\u0627\u06CC\u06CC \u062C\u062F\u0627\u06AF\u0627\u0646\u0647 \u0627\u0646\u062C\u0627\u0645 \u0645\u06CC\u200C\u0634\u0648\u062F.", "This bid is accepted. Choosing the final winner is a separate step.");
    $[14] = d.isReject;
    $[15] = d.isSetWinner;
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const effect = t4;
  let t5;
  if ($[18] !== d || $[19] !== onClose || $[20] !== t || $[21] !== title) {
    t5 = d.step === "result" ? <div className="as-dlg-foot"><button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={onClose}>{t("\u0628\u0633\u062A\u0646", "Close")}</button></div> : <div className="as-dlg-foot"><button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={onClose} disabled={d.pending}>{t("\u0627\u0646\u0635\u0631\u0627\u0641", "Cancel")}</button><ActionButton chain={true} className={d.isReject ? "as-danger" : undefined} onClick={() => void d.submit()} busy={d.pending} ariaLabel={d.pending ? undefined : `${title} — ${t("\u0627\u06CC\u0646 \u06A9\u0627\u0631 \u0628\u0631\u06AF\u0634\u062A\u200C\u067E\u0630\u06CC\u0631 \u0646\u06CC\u0633\u062A", "this cannot be undone")}`} busyLabel={d.isReject ? t("\u062F\u0631 \u062D\u0627\u0644 \u0631\u062F\u2026", "Rejecting\u2026") : d.isSetWinner ? t("\u062F\u0631 \u062D\u0627\u0644 \u0627\u0646\u062A\u062E\u0627\u0628 \u0628\u0631\u0646\u062F\u0647\u2026", "Choosing\u2026") : t("\u062F\u0631 \u062D\u0627\u0644 \u062A\u0623\u06CC\u06CC\u062F\u2026", "Accepting\u2026")}>{title}</ActionButton></div>;
    $[18] = d;
    $[19] = onClose;
    $[20] = t;
    $[21] = title;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const footer = t5;
  let t6;
  if ($[23] !== t) {
    t6 = t("\u0628\u0633\u062A\u0646", "Close");
    $[23] = t;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== bid || $[26] !== d.bidderName || $[27] !== d.currencySymbol || $[28] !== d.result || $[29] !== d.step || $[30] !== effect || $[31] !== isFa || $[32] !== t) {
    t7 = d.step === "result" && d.result ? <div className="as-result" data-outcome={d.result.outcome} role="status">{d.result.outcome === "success" ? <CircleCheck aria-hidden={true} /> : <CircleAlert aria-hidden={true} />}<div><b>{d.result.title}</b>{d.result.description ? <p>{d.result.description}</p> : null}</div></div> : <><dl className="as-list"><div><dt>{t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u062F\u0647\u0646\u062F\u0647", "Bidder")}</dt><dd>{d.bidderName}</dd></div><div className="as-list-lead"><dt>{t("\u0645\u0628\u0644\u063A", "Amount")}</dt><dd><bdi>{formatPrice(bid.price, isFa)}</bdi> {d.currencySymbol}</dd></div></dl><p className="as-dlg-lead">{effect}</p><p className="as-final"><LockKeyhole aria-hidden={true} />{t("\u0627\u06CC\u0646 \u06A9\u0627\u0631 \u0628\u0631\u06AF\u0634\u062A\u200C\u067E\u0630\u06CC\u0631 \u0646\u06CC\u0633\u062A.", "This can't be undone.")}</p></>;
    $[25] = bid;
    $[26] = d.bidderName;
    $[27] = d.currencySymbol;
    $[28] = d.result;
    $[29] = d.step;
    $[30] = effect;
    $[31] = isFa;
    $[32] = t;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== d.pending || $[35] !== footer || $[36] !== onClose || $[37] !== t6 || $[38] !== t7 || $[39] !== title) {
    t8 = <Dialog open={true} onClose={onClose} title={title} closeLabel={t6} footer={footer} className="as-dialog" busy={d.pending}>{t7}</Dialog>;
    $[34] = d.pending;
    $[35] = footer;
    $[36] = onClose;
    $[37] = t6;
    $[38] = t7;
    $[39] = title;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  return t8;
}